import { useCallback, useEffect, useRef, useState } from "react";
import type { ExtensionConnectionStatus } from "@cross-border-agent/contracts";
import { connectionView, createExtensionConnectionClient } from "./extension-connection";

export function ExtensionConnection({ onInstall, onUpdate }: { onInstall: () => void; onUpdate: () => void }) {
  const [status, setStatus] = useState<ExtensionConnectionStatus>();
  const [checking, setChecking] = useState(true);
  const [opening, setOpening] = useState(false);
  const [openNotice, setOpenNotice] = useState("");
  const client = useRef<ReturnType<typeof createExtensionConnectionClient> | undefined>(undefined);
  const busy = useRef(false);
  const check = useCallback(async () => {
    const current = client.current;
    if (!current || busy.current) return;
    busy.current = true; setChecking(true);
    try {
      const reply = await current.request("status");
      if (current === client.current) setStatus(reply.kind === "status" ? reply.status : undefined);
    } catch { if (current === client.current) setStatus(undefined); }
    finally { if (current === client.current) { busy.current = false; setChecking(false); } }
  }, []);
  useEffect(() => {
    const current = createExtensionConnectionClient(); client.current = current; busy.current = false;
    void check();
    const visible = () => { if (document.visibilityState === "visible") void check(); };
    const interval = setInterval(visible, 30_000);
    window.addEventListener("focus", visible); document.addEventListener("visibilitychange", visible);
    return () => { client.current = undefined; current.dispose(); clearInterval(interval); window.removeEventListener("focus", visible); document.removeEventListener("visibilitychange", visible); };
  }, [check]);
  const open = async () => {
    const current = client.current;
    if (!current || opening) return;
    setOpening(true); setOpenNotice("");
    try {
      const reply = status?.canOpen ? await current.request("open") : undefined;
      if (current === client.current) setOpenNotice(reply?.kind === "opened" ? "已请求打开右侧小满。请在面板内登录或直接提问；未看到时可点击浏览器工具栏的“满”图标。" : "当前浏览器未能从网页打开侧栏。请点击浏览器右上角的扩展按钮，再点击“小满陪跑”的“满”图标。若仍无反应，请更新浏览器和插件。 ");
    } catch { if (current === client.current) setOpenNotice("插件连接可能已中断。请刷新本页后重试，或点击浏览器工具栏的“满”图标打开小满。"); }
    finally { if (current === client.current) setOpening(false); }
  };
  const view = connectionView(status, import.meta.env.VITE_API_BASE_URL || "http://127.0.0.1:3000", checking);
  const authLabels = { signed_out: "未登录", authenticated: "已验证", refresh_required: "待续期", expired: "已失效", unknown: "待确认" };
  const serviceLabels = { ready: "基础检查通过", unavailable: "暂不可用", unconfigured: "配置未完成", demo: "模拟模式" };
  return <section className={`extension-connection is-${view.key}`} aria-label="插件连接与使用状态" aria-busy={checking}>
    <div className="extension-connection-state" role="status" aria-live="polite"><span className="extension-connection-dot" aria-hidden="true" /><strong>{view.title}</strong></div>
    <p>{view.detail}</p>
    {status && !checking && <dl><div><dt>插件版本</dt><dd>v{status.version}</dd></div><div><dt>插件登录</dt><dd>{authLabels[status.auth]}</dd></div><div><dt>服务</dt><dd>{serviceLabels[status.service]}</dd></div></dl>}
    <div className="extension-connection-actions">
      {status && !checking && view.key !== "update" && <button type="button" className="extension-guide-primary" onClick={() => void open()} disabled={opening}>{opening ? "正在打开…" : ["login", "expired"].includes(view.key) ? "打开小满登录" : "打开小满"}</button>}
      {!checking && !status && <button type="button" className="extension-guide-button" onClick={onInstall}>查看安装说明</button>}
      {!checking && (view.key === "update" || view.key === "environment" || !status) && <button type="button" className="extension-guide-button" onClick={onUpdate}>更新插件</button>}
      <button type="button" className="extension-guide-button" onClick={() => void check()} disabled={checking}>{checking ? "检测中…" : "重新检测"}</button>
    </div>
    {openNotice && <p className="extension-connection-open-notice" role="status">{openNotice}</p>}
    <small>仅检测当前浏览器。不会自动截图、发送问题或消耗积分。</small>
  </section>;
}
